import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Paperclip, File as FileIcon, Trash2, Download, Loader2, FileText, Image as ImageIcon, FileVideo } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

function iconForMime(mime, name = "") {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return FileVideo;
  if (mime === "application/pdf") return FileText;
  if (mime.includes("word") || mime.includes("excel") || mime.includes("sheet")) return FileText;
  if (name.toLowerCase().endsWith(".dcm")) return FileText;
  return FileIcon;
}

function humanSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Reusable file uploader + list.
 * Props: patientId, resourceType, resourceId, label, allowDelete
 */
export default function FileUpload({
  patientId,
  resourceType,
  resourceId,
  label = "Attached files",
  allowDelete = true,
  compact = false,
  readOnly = false,
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (patientId) params.set("patient_id", patientId);
    if (resourceType) params.set("resource_type", resourceType);
    if (resourceId) params.set("resource_id", resourceId);
    const { data } = await api.get(`/attachments?${params.toString()}`);
    setFiles(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, resourceType, resourceId]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        const fd = new FormData();
        fd.append("file", file);
        const params = new URLSearchParams();
        if (patientId) params.set("patient_id", patientId);
        if (resourceType) params.set("resource_type", resourceType);
        if (resourceId) params.set("resource_id", resourceId);
        await api.post(`/uploads?${params.toString()}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(fileList.length > 1 ? `${fileList.length} files uploaded` : "File uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleUpload(Array.from(e.dataTransfer.files));
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/attachments/${f.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = f.original_name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error("Could not download");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.original_name}?`)) return;
    try {
      await api.delete(`/attachments/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="space-y-2" data-testid={`file-upload-${resourceType || "general"}`}>
      {label && <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</div>}

      {!readOnly && (
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg cursor-pointer transition-colors text-center
          ${dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/40"}
          ${compact ? "p-3" : "p-5"}`}
        data-testid={`file-upload-zone-${resourceType || "general"}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.dcm,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov"
          data-testid={`file-upload-input-${resourceType}`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Uploading…
          </div>
        ) : (
          <div className={`flex ${compact ? "items-center justify-center gap-2" : "flex-col items-center gap-1.5"}`}>
            <Upload className={`${compact ? "size-4" : "size-5"} text-primary`} />
            <div className="text-sm">
              <span className="font-medium">Click to upload</span>
              <span className="text-muted-foreground"> or drag &amp; drop</span>
            </div>
            {!compact && <div className="text-[11px] text-muted-foreground">PDF, images, DICOM, Word, Excel · max 25MB</div>}
          </div>
        )}
      </div>
      )}

      {files.length === 0 && readOnly && (
        <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-lg">
          No files shared yet.
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-1.5 mt-2">
          {files.map((f) => {
            const Icon = iconForMime(f.mime, f.original_name);
            return (
              <div key={f.id} className="flex items-center gap-3 p-2.5 border border-border rounded-md group hover:bg-muted/40" data-testid={`file-row-${f.id}`}>
                <Icon className="size-4 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{f.original_name}</div>
                  <div className="text-[10px] text-muted-foreground mono">
                    {humanSize(f.size)} · {f.uploaded_by_name || "—"} · {new Date(f.created_at).toLocaleString()}
                  </div>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => download(f)} title="Download" data-testid={`file-download-${f.id}`}>
                  <Download className="size-4" />
                </Button>
                {allowDelete && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => remove(f)} className="text-destructive opacity-0 group-hover:opacity-100" data-testid={`file-delete-${f.id}`}>
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
